import React, { useState, useContext } from 'react'; // No need for useEffect here
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
// --- Add imports for icons and InputAdornment ---
import { TextField, Button, Box, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import validator from 'validator';
import './ResetPasswordPage.css';
import { Helmet } from 'react-helmet-async';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import { AuthContext } from '../../contexts/AuthContext';

const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const { token } = useParams();
    const navigate = useNavigate();
    const { fetchWithAuth } = useContext(AuthContext);

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [isTokenValid, setIsTokenValid] = useState(true);

    // --- Add state for visibility toggles ---
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handlePasswordChange = (value) => {
        setPassword(value);
        if (validator.isStrongPassword(value, { minLength: 8, minLowercase: 1, minUppercase: 1, minNumbers: 1, minSymbols: 1 })) {
            setPasswordError('');
        } else {
            setPasswordError(t('resetPasswordPage.passwordStrength.notStrong'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setNotification({ message: '', type: '' });

        if (password !== confirmPassword) {
            setNotification({ message: t('resetPasswordPage.notifications.passwordsDoNotMatch'), type: 'error' });
            return;
        }
        if (passwordError) {
            setNotification({ message: t('resetPasswordPage.notifications.passwordNotStrong'), type: 'error' });
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetchWithAuth('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'An error occurred.');

            // --- Map the backend key to the translation key ---
            let displayMessage = data.message;
            if (data.message === 'PASSWORD_RESET_SUCCESS') {
                displayMessage = t('resetPasswordPage.notifications.success');
            }

            setNotification({ message: displayMessage, type: 'success' });
           setTimeout(() => {
                navigate('/login');
            }, 3000);

        } catch (err) {
            // --- Map backend error code to the translation key ---
            let errorMessage = err.message;
            if (err.message === 'Invalid or expired token. Please try again.') {
                errorMessage = t('resetPasswordPage.notifications.invalidToken');
            }
            setNotification({ message: errorMessage, type: 'error' });
           setIsTokenValid(false);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Helper for the icon button click
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    return (
        // A React fragment is needed to hold both the nav and the content
        <> 
            <Helmet>
                <title>{t('resetPasswordPage.helmet.title')}</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            <UniversalNav />
            <div className="reset-page-container">
                <Box component="form" onSubmit={handleSubmit} className="reset-panel">
                    <h2>{t('resetPasswordPage.mainTitle')}</h2>
                    {isTokenValid ? (
                        <>
                            <p className="subtitle">{t('resetPasswordPage.subtitle')}</p>
                            <TextField
                                label={t('resetPasswordPage.labels.newPassword')}
                                // Use the state to toggle type
                                type={showPassword ? 'text' : 'password'}
                                fullWidth
                                margin="normal"
                                value={password}
                                onChange={(e) => handlePasswordChange(e.target.value)}
                                required
                                error={!!passwordError}
                                helperText={passwordError}
                                // --- Add the InputAdornment with the icon ---
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword((show) => !show)}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                label={t('resetPasswordPage.labels.confirmNewPassword')}
                                // Use the second state variable to toggle type
                                type={showConfirmPassword ? 'text' : 'password'}
                                fullWidth
                                margin="normal"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                // --- Add the InputAdornment with the icon ---
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle confirm password visibility"
                                                onClick={() => setShowConfirmPassword((show) => !show)}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {notification.message && (
                                <Alert severity={notification.type} sx={{ width: '100%', mt: 2 }}>
                                    {notification.message}
                                </Alert>
                            )}
                            <Button type="submit" variant="contained" fullWidth disabled={isLoading} sx={{ mt: 3, py: 1.5 }}>
                                {isLoading ? <CircularProgress size={24} /> : t('resetPasswordPage.buttons.reset')}
                            </Button>
                        </>
                    ) : (
                        <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
                            {t('resetPasswordPage.notifications.invalidToken')}
                        </Alert>
                    )}
                </Box>
            </div>
        </>
    );
};

export default ResetPasswordPage;